/**
 * Classes & Sports is a "type-first" hub. The Tier 1 layer is a page per
 * activity type — because "swim lessons santa barbara" is searched far more
 * than any individual studio's name.
 */

export const classesIntro = {
  count: '63 programs · 6 activity types',
  updated: 'Registration windows checked monthly',
  deck:
    'Swim, dance, gymnastics, soccer, music and martial arts — every program in the county, with what it costs, what age it starts, and the week registration actually opens. That last one is the whole reason this page exists.',
}

export interface OpenNow {
  program: string
  activity: string
  closes: string
  urgency: 'now' | 'soon' | 'waitlist'
}

export const openNow: OpenNow[] = [
  { program: 'Junior Lifeguards, City of SB', activity: 'Swim', closes: 'Closes Mar 14', urgency: 'now' },
  { program: 'AYSO Region 122 fall soccer', activity: 'Soccer', closes: 'Closes Jun 1', urgency: 'soon' },
  { program: 'SB Gymnastics summer session', activity: 'Gymnastics', closes: 'Waitlist only', urgency: 'waitlist' },
]

export interface ActivityType {
  slug: string
  name: string
  programs: number
  ages: string
  signUps: string
  blurb: string
  swatch: string
}

export const activityTypes: ActivityType[] = [
  {
    slug: 'swim-lessons-santa-barbara',
    name: 'Swim lessons',
    programs: 14,
    ages: '6 months – 14',
    signUps: 'Opens February',
    blurb:
      'Year-round indoor pools, summer-only outdoor sessions, and the two programs with a genuine waitlist problem.',
    swatch: 'bg-opal',
  },
  {
    slug: 'kids-dance-classes-santa-barbara',
    name: 'Dance',
    programs: 11,
    ages: '18 months – 18',
    signUps: 'Rolling',
    blurb:
      'Toddler creative movement through pre-professional ballet, plus which studios do a low-pressure recital.',
    swatch: 'bg-peach',
  },
  {
    slug: 'kids-gymnastics-santa-barbara',
    name: 'Gymnastics',
    programs: 6,
    ages: '12 months – 16',
    signUps: 'Opens August & January',
    blurb:
      'Parent-and-me classes, open gym times, and the birthday party options nobody advertises well.',
    swatch: 'bg-foam',
  },
  {
    slug: 'youth-soccer-santa-barbara',
    name: 'Soccer',
    programs: 9,
    ages: '3 – 18',
    signUps: 'Opens April',
    blurb:
      'Rec leagues, club tryouts and drop-in Saturday programs, sorted by how much of your weekend they take.',
    swatch: 'bg-olive',
  },
  {
    slug: 'kids-music-classes-santa-barbara',
    name: 'Music',
    programs: 13,
    ages: '0 – 18',
    signUps: 'Rolling',
    blurb:
      'Baby music circles, group lessons and private teachers who are actually taking new students.',
    swatch: 'bg-creme',
  },
  {
    slug: 'kids-martial-arts-santa-barbara',
    name: 'Martial arts',
    programs: 10,
    ages: '4 – 18',
    signUps: 'Rolling',
    blurb:
      'Karate, jiu jitsu and taekwondo, with an honest read on contract length before you sign anything.',
    swatch: 'bg-opal',
  },
]

export interface Program {
  id: string
  name: string
  activity: string
  area: string
  ages: string
  price: string
  session: string
  blurb: string
  tags: string[]
}

export const standoutPrograms: Program[] = [
  {
    id: 'c1',
    name: 'Santa Barbara Junior Lifeguards',
    activity: 'Swim',
    area: 'East Beach',
    ages: '9–15',
    price: '$425 / 3 weeks',
    session: 'Summer only',
    blurb:
      'The rite of passage. Sessions sell out the first morning registration opens, so set a calendar reminder for March 1.',
    tags: ['Swim', 'Summer', 'Big kids', 'Drop-off'],
  },
  {
    id: 'c2',
    name: 'Page Youth Center',
    activity: 'Multiple',
    area: 'Downtown',
    ages: '3–14',
    price: '$120–$260 / season',
    session: 'Year-round',
    blurb:
      'Basketball, volleyball and after-school programs under one roof. The most affordable organized sport in town.',
    tags: ['Soccer', 'Big kids', 'Drop-off', 'Year-round'],
  },
  {
    id: 'c3',
    name: 'Rhythmix Dance Studio',
    activity: 'Dance',
    area: 'Goleta',
    ages: '18 months–12',
    price: '$85 / month',
    session: 'Rolling enrollment',
    blurb:
      'The gentlest entry point for a shy toddler. Optional recital, which matters more than you would think at three.',
    tags: ['Dance', 'Toddler-friendly', 'Year-round'],
  },
  {
    id: 'c4',
    name: 'SB Gymnastics Club',
    activity: 'Gymnastics',
    area: 'The Mesa',
    ages: '12 months–16',
    price: '$110 / month',
    session: 'Fall & spring terms',
    blurb:
      'Parent-and-me on weekday mornings, competitive team by invitation. Open gym Friday nights is the best six dollars in town.',
    tags: ['Gymnastics', 'Toddler-friendly', 'Birthday parties'],
  },
  {
    id: 'c5',
    name: 'AYSO Region 122',
    activity: 'Soccer',
    area: 'Countywide',
    ages: '4–14',
    price: '$180 / season',
    session: 'Fall',
    blurb:
      'Everyone plays, nobody gets cut, and half your kid’s class will be on the field. Registration opens in April and closes in June.',
    tags: ['Soccer', 'Big kids', 'Fall'],
  },
  {
    id: 'c6',
    name: 'Music Together Santa Barbara',
    activity: 'Music',
    area: 'Downtown & Goleta',
    ages: '0–5',
    price: '$230 / 10 weeks',
    session: 'Rolling',
    blurb:
      'Circle-time music with a parent in the room. Slightly expensive, genuinely lovely, and siblings under one are free.',
    tags: ['Music', 'Toddler-friendly', 'Year-round'],
  },
]

export const classesTier3 = [
  {
    area: 'Swim & water',
    items: ['SB Family YMCA', 'Los Baños del Mar', 'Cathedral Oaks Athletic Club', 'Goleta Valley Athletic Club'],
  },
  {
    area: 'Dance & theater',
    items: ['State Street Ballet Academy', 'Gustafson Dance', 'Santa Barbara Dance Arts', 'Boxtales Theatre'],
  },
  {
    area: 'Team sports',
    items: ['SB Youth Basketball', 'Carpinteria Little League', 'Goleta Valley Girls Softball', 'SB Youth Rugby'],
  },
  {
    area: 'Art, music & other',
    items: ['SB Museum of Art classes', 'Notes Music Studio', 'Adventure Chess Club', 'Coding for Kids SB'],
  },
]

export const classesFilters = [
  { group: 'Activity', options: ['Swim', 'Dance', 'Gymnastics', 'Soccer', 'Music'] },
  { group: 'Age', options: ['Toddler-friendly', 'Big kids'] },
  { group: 'When', options: ['Year-round', 'Summer', 'Fall'] },
  { group: 'Format', options: ['Drop-off', 'Birthday parties'] },
]

export const classesFaqs = [
  {
    question: 'When does registration open for Santa Barbara kids’ programs?',
    answer:
      'It varies more than it should. Junior Lifeguards opens March 1 and sells out the same day. AYSO soccer opens in April for the fall season. Swim lessons open in February for summer. Dance, music and martial arts are mostly rolling, so you can start any month.',
  },
  {
    question: 'What is the cheapest organized activity for kids here?',
    answer:
      'Page Youth Center seasons start around $120, and AYSO soccer is $180 for a full fall season including the uniform. City of Santa Barbara Recreation classes are also well under private-studio pricing.',
  },
  {
    question: 'What can a two-year-old actually do?',
    answer:
      'Parent-and-me gymnastics, creative movement dance, and music circles all start between twelve and eighteen months. Swim lessons start at six months, though most instructors will tell you the real learning begins around three.',
  },
]
